import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const hasTextRangeSelection = (): boolean => {
  const selection = globalThis.getSelection()

  if (!selection) {
    return false
  }

  return selection.type === "Range" && selection.toString().trim().length > 0
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        muted: "border border-input bg-muted text-foreground shadow-none hover:bg-muted/80",
        iconMuted: "rounded-full bg-muted text-muted-foreground shadow-none hover:text-foreground",
        selectCard:
          "h-auto w-full justify-start rounded-lg p-5 text-left whitespace-normal bg-muted text-foreground shadow-none ring-1 ring-border/40 transition-all duration-200 hover:scale-[1.02] hover:bg-primary/10 hover:ring-primary hover:shadow-lg active:scale-[0.98]",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        none: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Prevent `onClick` when the user is selecting text with the pointer or holding `Alt`.
   *
   * @remarks
   * This is opt-in to avoid changing default button behavior globally.
   */
  suppressClickWhenSelectingText?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, suppressClickWhenSelectingText = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      if (suppressClickWhenSelectingText && (event.altKey || hasTextRangeSelection())) {
        return
      }

      onClick?.(event)
    }

    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} onClick={handleClick} {...props} />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
